// Denial worklist — the claims that came back rejected or denied, and the record
// of who is chasing each one.
//
// Registry page (features/registry.js, `feature: 'worklist'`). App.js renders
// <FeatureOff feature="worklist"/> when the feature is off, so this page never has
// to check the gate to decide whether to exist.
//
// Follow-up rows are created by the sweep (backend/scripts/followup_sweep.py, and
// after every claim import for the importing practice), not by this page. What a biller does here is
// claim one, say what happens next, and close it. Editing is per-field and saves on
// blur: the backend PUT only changes the keys present in the body, so two people
// working different columns of the same row cannot overwrite each other.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./WorklistPage.css";
import { formatCurrency } from "../../utils/formatCurrency";
import { formatPatientName } from "../../utils/formatName";
import { DATE_INPUT_MIN, DATE_INPUT_MAX } from "../../utils/dateInputBounds";
import denialCodes from "./denialCodes.json";

const API_URL = process.env.REACT_APP_API_URL || "";

// Same vocabulary as models_followups.REASON_BUCKETS. The label says what a biller
// would DO about it, because that is the reason the bucket exists.
const BUCKETS = [
  {
    key: "coding",
    label: "Coding",
    hint: "The claim was built wrongly — recode and resubmit.",
  },
  {
    key: "eligibility",
    label: "Eligibility",
    hint: "The patient was not covered for this on this date.",
  },
  {
    key: "enrollment",
    label: "Enrollment",
    hint: "The provider is not set up with this payer.",
  },
  {
    key: "auth",
    label: "Auth",
    hint: "Authorisation was missing, invalid or exceeded.",
  },
  {
    key: "timely",
    label: "Timely filing",
    hint: "Past the filing limit. Usually stop rather than fix.",
  },
  {
    key: "other",
    label: "Other",
    hint: "No bucket fits yet — read the reason.",
  },
];

const STATUS_FILTERS = [
  { key: "open", label: "Open" },
  { key: "working", label: "Working" },
  { key: "closed", label: "Closed" },
  { key: "all", label: "All" },
];

const STATUSES = ["open", "working", "resolved", "wontfix"];

// Plain-language help for a CARC/RARC, from denialCodes.json. Remits write the same
// code as "CO-B7", "COB7" or "co b7"; the table is keyed without a separator.
function codeHelp(code) {
  const key = String(code || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
  return denialCodes.codes[key] || null;
}

// The server's split of the claim's codes (denial_service.split_adjustment_codes):
// the denial-group adjustments and remarks under "Codes", every PR code under
// "Patient responsibility". This page renders the two lists; it does not classify.
function rowCodes(row) {
  return row.claim.denial_codes || [];
}

function patientCodes(row) {
  return row.claim.patient_responsibility_codes || [];
}

function codeAmount(entry) {
  if (entry.amount === null || entry.amount === undefined) return "";
  if (Math.abs(Number(entry.amount)) < 0.01) return "";
  return formatCurrency(entry.amount);
}

function CodeList({ codes }) {
  return (
    <ul className="worklist-codes">
      {codes.map((entry) => {
        const help = codeHelp(entry.code);
        const detail = [entry.label, codeAmount(entry)]
          .filter(Boolean)
          .join(" ");
        return (
          <li key={entry.code}>
            <span className="code-chip">{entry.code}</span>
            {detail && <span className="worklist-code-detail">{detail}</span>}
            {help && (
              <span className="denial-code-help">
                {help.meaning}
                <span className="denial-code-check">
                  Check first: {help.check_first}
                </span>
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function bucketLabel(key) {
  const found = BUCKETS.find((b) => b.key === key);
  return found ? found.label : key || "-";
}

export default function WorklistPage({ selectedPractice, readOnly = false }) {
  const practiceId = selectedPractice?.id;

  const [rows, setRows] = useState([]);
  // How many people work this practice's claims. One (or none) and the Owner column is
  // hidden: "assign to" is optional, and a column nobody fills is clutter (David's walk,
  // 2026-09-15). A `users` count, not a setting.
  const [showOwner, setShowOwner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [status, setStatus] = useState("open");
  const [bucket, setBucket] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Local edits, keyed by follow-up id, so a field being typed into is not
  // overwritten by a refetch mid-edit.
  const [drafts, setDrafts] = useState({});

  // Every request takes a ticket; only the newest one may write — the same rule
  // ClaimsLedgerPage.loadClaims uses. Without it, switching practice mid-flight
  // paints practice A's denials under practice B's name.
  const ticket = useRef(0);

  const fetchRows = useCallback(async () => {
    if (!practiceId) return;
    const mine = (ticket.current += 1);
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ status });
      if (bucket) params.set("bucket", bucket);
      const resp = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/worklist?${params.toString()}`,
        { credentials: "include" },
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(
          body.error || `Could not load the worklist (${resp.status})`,
        );
      }
      const body = await resp.json().catch(() => null);
      if (mine !== ticket.current) return;
      // A 200 is not a payload. A truncated body or a proxy's HTML error page
      // parses to null, or to an object without `worklist`, and `|| []` would then
      // render the "nothing to work" empty state — an all-clear produced at
      // exactly the moment we could not check. Refuse instead.
      if (!body || !Array.isArray(body.worklist)) {
        throw new Error(
          "The response could not be read, so this list may be incomplete. Try again.",
        );
      }
      setRows(body.worklist);
      // Hide the column only when the server SAYS one person works this practice. A
      // missing count, or a row that already carries an owner, keeps it visible —
      // hiding an assignment somebody made would leave it unreadable and unclearable.
      const count = body.biller_count;
      const assigned = body.worklist.some((row) => (row.followup || {}).owner);
      setShowOwner(
        count === undefined || count === null || count > 1 || assigned,
      );
      // Drafts are NOT cleared here. A refresh that discarded what a biller was
      // halfway through typing is the opposite of what the per-field save is for;
      // a draft is dropped when its own save succeeds, and nowhere else.
    } catch (err) {
      if (mine !== ticket.current) return;
      setError(err.message);
      setRows([]);
    } finally {
      if (mine === ticket.current) setLoading(false);
    }
  }, [practiceId, status, bucket]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const counts = useMemo(() => {
    const out = {};
    rows.forEach((row) => {
      const key = row.followup?.reason_bucket || "other";
      out[key] = (out[key] || 0) + 1;
    });
    return out;
  }, [rows]);

  const overdueCount = useMemo(
    () => rows.filter((r) => r.overdue).length,
    [rows],
  );

  const save = async (row, fields) => {
    const claimId = row.claim.id;
    setSavingId(row.followup.id);
    setMessage(null);
    try {
      const resp = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/${claimId}/followup`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fields),
        },
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || `Could not save (${resp.status})`);
      }
      const body = await resp.json();
      setRows((current) =>
        current
          .map((r) =>
            r.claim.id === claimId ? { ...r, followup: body.followup } : r,
          )
          // A row that has just been closed leaves an open list immediately, so the
          // worklist visibly shrinks as it is worked.
          .filter((r) => {
            if (status !== "open" && status !== "working") return true;
            return ["open", "working"].includes(r.followup.status);
          }),
      );
      setDrafts((d) => {
        const next = { ...d };
        delete next[body.followup.id];
        return next;
      });
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setSavingId(null);
    }
  };

  const draftFor = (row, field) => {
    const draft = drafts[row.followup.id];
    if (draft && field in draft) return draft[field];
    return row.followup[field] || "";
  };

  const setDraft = (row, field, value) =>
    setDrafts((d) => ({
      ...d,
      [row.followup.id]: { ...(d[row.followup.id] || {}), [field]: value },
    }));

  const commit = (row, field) => {
    const draft = drafts[row.followup.id];
    if (!draft || !(field in draft)) return;
    if ((draft[field] || "") === (row.followup[field] || "")) return;
    save(row, { [field]: draft[field] });
  };

  if (!practiceId) {
    return (
      <div className="worklist-page">
        <p className="worklist-empty">
          Select a practice to see its denial worklist.
        </p>
      </div>
    );
  }

  return (
    <div className="worklist-page">
      <div className="worklist-header">
        <div>
          <h1>Denial worklist</h1>
          <p className="worklist-sub">
            Rejected and denied claims for {selectedPractice.name}, newest date
            of service first. Follow-ups are opened overnight; closing one takes
            it off this list.
          </p>
        </div>
        <button
          className="btn btn-secondary btn-sm"
          onClick={fetchRows}
          disabled={loading}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {message && (
        <div className={`worklist-message ${message.type}`}>
          {message.text}
          <button
            className="worklist-message-close"
            onClick={() => setMessage(null)}
          >
            ×
          </button>
        </div>
      )}

      <div className="worklist-filters">
        <div className="worklist-tabs">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              className={`worklist-tab ${status === f.key ? "active" : ""}`}
              onClick={() => setStatus(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <select
          className="worklist-select"
          value={bucket}
          onChange={(e) => setBucket(e.target.value)}
          aria-label="Filter by reason"
        >
          <option value="">All reasons</option>
          {BUCKETS.map((b) => (
            <option key={b.key} value={b.key}>
              {b.label}
              {counts[b.key] ? ` (${counts[b.key]})` : ""}
            </option>
          ))}
        </select>
        <div className="worklist-counts">
          <span>
            {rows.length} claim{rows.length === 1 ? "" : "s"}
          </span>
          {overdueCount > 0 && (
            <span className="worklist-overdue-count">
              {overdueCount} overdue
            </span>
          )}
        </div>
      </div>

      {error && <div className="worklist-message error">{error}</div>}

      {!loading && !error && rows.length === 0 && (
        <p className="worklist-empty">
          Nothing to work. Either no claim has come back rejected or denied, or
          every follow-up is closed.
        </p>
      )}

      {rows.length > 0 && (
        <div className="worklist-table-wrap">
          <table className="worklist-table">
            <thead>
              <tr>
                <th>Claim</th>
                <th>Patient</th>
                <th>Payer</th>
                <th>DOS</th>
                <th className="num">Billed</th>
                <th>Reason</th>
                {showOwner && <th>Owner</th>}
                <th>Next action</th>
                <th>Due</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const f = row.followup;
                const busy = savingId === f.id;
                return (
                  <React.Fragment key={f.id}>
                    <tr
                      className={`worklist-row ${row.overdue ? "overdue" : ""} ${busy ? "saving" : ""}`}
                    >
                      <td>
                        <button
                          className="worklist-claim-link"
                          onClick={() =>
                            setExpandedId(expandedId === f.id ? null : f.id)
                          }
                          title="Show the rejection reason"
                        >
                          #{row.claim.id}
                        </button>
                      </td>
                      <td className="worklist-patient">
                        {formatPatientName(row.claim.patient_name) || "-"}
                      </td>
                      <td>{row.claim.payer_name || "-"}</td>
                      <td>{row.claim.date_of_service || "-"}</td>
                      <td className="num">
                        {formatCurrency(row.claim.billed_amount)}
                      </td>
                      <td>
                        <span
                          className={`bucket-chip bucket-${f.reason_bucket || "other"}`}
                        >
                          {bucketLabel(f.reason_bucket)}
                        </span>
                      </td>
                      {showOwner && (
                        <td>
                          <input
                            className="worklist-input"
                            type="text"
                            value={draftFor(row, "owner")}
                            placeholder="unclaimed"
                            disabled={readOnly || busy}
                            onChange={(e) =>
                              setDraft(row, "owner", e.target.value)
                            }
                            onBlur={() => commit(row, "owner")}
                          />
                        </td>
                      )}
                      <td>
                        <input
                          className="worklist-input wide"
                          type="text"
                          value={draftFor(row, "next_action")}
                          placeholder="what happens next"
                          title={draftFor(row, "next_action") || undefined}
                          disabled={readOnly || busy}
                          onChange={(e) =>
                            setDraft(row, "next_action", e.target.value)
                          }
                          onBlur={() => commit(row, "next_action")}
                        />
                      </td>
                      <td>
                        <input
                          className="worklist-input date"
                          type="date"
                          min={DATE_INPUT_MIN}
                          max={DATE_INPUT_MAX}
                          value={draftFor(row, "due_on")}
                          disabled={readOnly || busy}
                          onChange={(e) =>
                            setDraft(row, "due_on", e.target.value)
                          }
                          onBlur={() => commit(row, "due_on")}
                        />
                      </td>
                      <td>
                        <select
                          className="worklist-select sm"
                          value={f.status}
                          disabled={readOnly || busy}
                          onChange={(e) =>
                            save(row, { status: e.target.value })
                          }
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                    {expandedId === f.id && (
                      <tr className="worklist-detail-row">
                        <td colSpan={showOwner ? 10 : 9}>
                          <div className="worklist-detail">
                            <div>
                              <span className="worklist-detail-label">
                                Rejection reason
                              </span>
                              <div className="worklist-reason">
                                {row.rejection_reason ||
                                  "No details available."}
                              </div>
                            </div>
                            {row.closed_reason && (
                              <div>
                                <span className="worklist-detail-label">
                                  Closed
                                </span>
                                <div className="worklist-reason">
                                  {row.closed_reason}
                                </div>
                              </div>
                            )}
                            {rowCodes(row).length > 0 && (
                              <div>
                                <span className="worklist-detail-label">
                                  Codes
                                </span>
                                <CodeList codes={rowCodes(row)} />
                              </div>
                            )}
                            {patientCodes(row).length > 0 && (
                              <div>
                                <span className="worklist-detail-label">
                                  Patient responsibility
                                </span>
                                <CodeList codes={patientCodes(row)} />
                              </div>
                            )}
                            <div className="worklist-detail-hint">
                              {
                                BUCKETS.find((b) => b.key === f.reason_bucket)
                                  ?.hint
                              }
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
